<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import { useRouter } from 'vue-router/composables';
  import { useResizeObserver } from '@vueuse/core';
  import { IRestaurant } from '@/interfaces/IRestaurant';
  import { IBranch } from '@/interfaces/IBranch';
  import { vueData } from '@/VueData';
  import { PermissionType } from '@/enums/PermissionType';
  import V2RestaurantDashboardHeader from '@/components/v2/restaurant/dashboard/V2RestaurantDashboardHeader.vue';
  import V2RestaurantDashboardBranchSelector from '@/components/v2/restaurant/dashboard/V2RestaurantDashboardBranchSelector.vue';
  import V2RestaurantDashboardMetricCard from '@/components/v2/restaurant/dashboard/V2RestaurantDashboardMetricCard.vue';
  import V2RestaurantDashboardOrderOpsStatus from '@/components/v2/restaurant/dashboard/V2RestaurantDashboardOrderOpsStatus.vue';
  import V2RestaurantDashboardQuickActions from '@/components/v2/restaurant/dashboard/V2RestaurantDashboardQuickActions.vue';
  import V2RestaurantDashboardPerformanceChart from '@/components/v2/restaurant/dashboard/V2RestaurantDashboardPerformanceChart.vue';
  import V2RestaurantDashboardOnboardingMessages from '@/components/v2/restaurant/dashboard/V2RestaurantDashboardOnboardingMessages.vue';
  import V2RestaurantDashboardSystemMessages from '@/components/v2/restaurant/dashboard/V2RestaurantDashboardSystemMessages.vue';
  import V2RestaurantDashboardHeatmap from '@/components/v2/restaurant/dashboard/V2RestaurantDashboardHeatmap.vue';
  import V2RestaurantDashboardTopBottomItems from '@/components/v2/restaurant/dashboard/V2RestaurantDashboardTopBottomItems.vue';
  import V2RestaurantDashboardGpthelpPromo from '@/components/v2/restaurant/dashboard/V2RestaurantDashboardGpthelpPromo.vue';
  import V2StoreSetupChecklist from '@/components/v2/store-setup/dashboard/V2StoreSetupChecklist.vue';
  import V2StoreSetupMenuCard from '@/components/v2/store-setup/dashboard/V2StoreSetupMenuCard.vue';
  import { useMenuUpdateUndo } from '@/composition/menu-import/use-menu-update-undo';
  import V2StatsBusinessDayNote from '@/components/v2/stats/V2StatsBusinessDayNote.vue';
  import RestaurantsService from '@/api/services/restaurants.service';
  import type { IRestaurantSettings } from '@/api/services/restaurants.service';
  import StatsService from '@/api/services/stats.service';
  import { IStatsContext } from '@/interfaces/IStatsContext';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { shortWeekday } from '@/utils/intl-weekdays.utils';
  import { useStoreLiveLink } from '@/composition/store-setup/use-store-live-link';
  import { useStoreSetupStatus } from '@/composition/store-setup/use-store-setup-status';
  import { useGtag } from '@/composition/use-gtag';
  import { useUi } from '@/composition/use-ui';
  import { dashboardLayoutClasses } from '@/domain/dashboard/dashboard-layout';
  import {
    emptyMetricKeys,
    isChecklistVisible,
  } from '@/domain/store-setup/setup-dashboard';
  import { buildChecklist } from '@/domain/store-setup/setup-flow';
  import type { ISetupPatchDto } from '@/interfaces/IStoreSetup';
  import { AlertType } from '@/interfaces/IAlert';
  import {
    IBusinessDayAnchor,
    businessHourPosition,
    currentHourInTimeZone,
    hasBusinessDayCutoff,
    resolveBusinessDayAnchor,
  } from '@/domain/stats/business-day';

  // Props
  const props = withDefaults(
    defineProps<{
      restaurant: IRestaurant;
      restaurantSettings?: IRestaurantSettings | null;
    }>(),
    { restaurantSettings: null }
  );

  const restaurantStore = useRestaurantStore();
  const router = useRouter();
  const gtag = useGtag();
  const ui = useUi();

  // i18n in script context (script setup has no $t)
  const proxy = getCurrentInstance()!.proxy;

  // Services
  const restaurantsService = new RestaurantsService();
  const statsService = new StatsService();

  // Layout steps from the page's own width: the docked «Asistente» panel
  // narrows the page without changing the window.
  const root = ref<HTMLElement | null>(null);
  const width = ref(0);
  const layoutClasses = computed(() => dashboardLayoutClasses(width.value));

  function measure() {
    // offsetWidth: the padding a step sets never changes the step.
    if (root.value) width.value = root.value.offsetWidth;
  }

  // Synchronous first read: the first paint already has its layout.
  onMounted(measure);
  useResizeObserver(root, measure);

  // State
  const loading = ref(true);
  const itemCount = ref(0);
  const categoryCount = ref(0);

  // Daily metrics data
  const dailyMetrics = ref({
    orders: {
      count: 0,
      changeFromLastWeek: 0,
      loading: true,
      comparisonType: 'week',
    },
    revenue: {
      amount: 0,
      changeFromLastWeek: 0,
      loading: true,
      comparisonType: 'week',
      // Today's excluded non-monetary amount (invitaciones + consumo
      // empleado) and the gross total including it, for the info tooltip.
      nonMonetary: 0,
      gross: 0,
    },
    menuViews: {
      count: 0,
      changeFromLastWeek: 0,
      loading: true,
      comparisonType: 'week',
    },
    averageTicket: {
      amount: 0,
      changeFromLastWeek: 0,
      loading: true,
      comparisonType: 'week',
    },
    covers: {
      count: 0,
      changeFromLastWeek: 0,
      loading: true,
      comparisonType: 'week',
      // True when some sittings closed without a covers value, so the sum
      // is a lower bound rather than the real total.
      incomplete: false,
    },
    spendPerCover: {
      amount: 0,
      changeFromLastWeek: 0,
      loading: true,
      comparisonType: 'week',
      // Today's non-monetary amount excluded from the (net) spend average.
      nonMonetary: 0,
    },
  });

  // Store setup (plan §4.10): the checklist of flow stores, the «Su menú»
  // card and the teaching copy of empty metrics.
  const setupStatus = useStoreSetupStatus(() => props.restaurant?.id ?? null);
  const menuUpdateUndo = useMenuUpdateUndo();
  const setupState = setupStatus.status;
  // The link actions of the menu card and the «Visitas» metric (no QR shown).
  const storeLink = useStoreLiveLink(() => props.restaurant ?? null, {
    qr: false,
  });
  /** The setup status answered (or failed): until then neither card shows. */
  const setupSettled = ref(false);
  /** A checklist write is running (hide, «Ya lo hice»). */
  const setupBusy = ref(false);
  let setupLoadRun = 0;
  let alive = true;

  // Covers cards only make sense for restaurants that record covers: hidden
  // once loading finishes with zero covers in both compared periods. True
  // while loading so the skeletons render for restaurants that do use them.
  const showCoversCards = ref(true);

  // Computed
  const hasStatsPermission = computed(() => {
    return vueData.hasPermission(PermissionType.StatisticsView);
  });

  const multipleMenus = computed(
    () => props.restaurantSettings?.multipleMenus === true
  );

  // Flow stores show the checklist instead of the onboarding card until the
  // owner hides it; every other store keeps the onboarding card.
  const showChecklist = computed(
    () => setupSettled.value && isChecklistVisible(setupState.value)
  );

  const showOnboarding = computed(
    () => setupSettled.value && !showChecklist.value
  );

  const checklistRows = computed(() =>
    setupState.value
      ? buildChecklist(setupState.value, props.restaurant, multipleMenus.value)
      : []
  );

  const showMenuCard = computed(
    () =>
      !!setupState.value && vueData.hasPermission(PermissionType.CatalogManage)
  );

  const emptyMetrics = computed(() =>
    emptyMetricKeys({
      enabled: showChecklist.value,
      ordersCount: dailyMetrics.value.orders.count,
      revenueAmount: dailyMetrics.value.revenue.amount,
      viewsCount: dailyMetrics.value.menuViews.count,
    })
  );

  const isNewStore = computed(() => {
    if (!props.restaurant) return false;

    const createdAt = new Date(props.restaurant.createdAt);
    const now = new Date();
    const diff = now.getTime() - createdAt.getTime();
    const diffHours = diff / 1000 / 60 / 60;
    return (
      diffHours < 48 ||
      (!loading.value && (itemCount.value === 0 || categoryCount.value === 0))
    );
  });

  const hasWeekOfData = computed(() => {
    // For now, always use week comparison
    return true;

    if (!props.restaurant) return false;

    const createdAt = new Date(props.restaurant.createdAt);
    const now = new Date();
    const diff = now.getTime() - createdAt.getTime();
    const diffDays = diff / 1000 / 60 / 60 / 24;
    return diffDays >= 7;
  });

  // "12+" when some sittings closed without covers: the sum is a lower bound.
  const coversDisplayValue = computed(() => {
    const count = dailyMetrics.value.covers.count.toLocaleString();
    return dailyMetrics.value.covers.incomplete ? `${count}+` : count;
  });

  const currencySymbol = computed(
    () => restaurantStore.currency?.symbol || '$'
  );

  // Tooltips shown when today's totals exclude non-monetary amounts
  // (invitaciones / consumo empleado); undefined hides the info icon.
  const revenueInfoText = computed(() => {
    const { nonMonetary, gross } = dailyMetrics.value.revenue;
    if (nonMonetary <= 0) return undefined;
    return String(
      proxy.$t('dashboard.revenueNonMonetaryInfo', {
        amount: `${currencySymbol.value}${nonMonetary.toFixed(2)}`,
        total: `${currencySymbol.value}${gross.toFixed(2)}`,
      })
    );
  });

  const averageTicketInfoText = computed(() => {
    if (dailyMetrics.value.revenue.nonMonetary <= 0) return undefined;
    return String(proxy.$t('dashboard.averageTicketNetInfo'));
  });

  const spendPerCoverInfoText = computed(() => {
    const { nonMonetary } = dailyMetrics.value.spendPerCover;
    if (nonMonetary <= 0) return undefined;
    return String(
      proxy.$t('dashboard.spendPerCoverNonMonetaryInfo', {
        amount: `${currencySymbol.value}${nonMonetary.toFixed(2)}`,
      })
    );
  });

  /**
   * Branch business day, resolved once per load/branch switch and shared
   * with every widget (null until the first resolution).
   */
  const businessDay = ref<IBusinessDayAnchor | null>(null);
  // Guards out-of-order resolutions on rapid branch switches.
  let businessDayGeneration = 0;

  /** Business "today" of the resolved anchor (browser day until then). */
  const businessToday = computed(() => businessDay.value?.today ?? new Date());

  /**
   * Business-day note above the cards (start hour > 0 only). Hidden while a
   * restaurant/branch switch is still resolving, so the text and the pencil
   * always describe the selected branch.
   */
  const businessDayNote = computed(() => {
    const anchor = businessDay.value;
    const context = anchor?.context;
    return anchor &&
      context &&
      anchor.branchId === restaurantStore.selectedBranch?.id &&
      hasBusinessDayCutoff(context)
      ? { context, branchId: anchor.branchId }
      : null;
  });

  const getComparisonLabel = computed(() => {
    return (comparisonType: string) => {
      if (comparisonType === 'week') {
        return String(
          proxy.$t('dashboard.vsLastWeekday', {
            day: shortWeekday(proxy.$i18n.locale, businessToday.value),
          })
        );
      }
      return String(proxy.$t('dashboard.vsYesterday'));
    };
  });

  // Methods
  const emptyText = (key: string | null) =>
    key ? String(proxy.$t(key)) : undefined;

  const loadSetupStatus = async () => {
    const run = ++setupLoadRun;
    setupSettled.value = false;
    await setupStatus.load();
    if (alive && run === setupLoadRun) setupSettled.value = true;
  };

  /** «Deshacer» of the latest update from photos: the card then shows the menu as it is. */
  const undoMenuUpdate = async (sessionId: string) => {
    const result = await menuUpdateUndo.undo(sessionId);
    if (alive && result !== 'cancelled') void loadSetupStatus();
  };

  const patchSetup = async (body: ISetupPatchDto) => {
    if (setupBusy.value) return false;
    setupBusy.value = true;
    const patched = await setupStatus.patch(body);
    if (alive) setupBusy.value = false;
    return patched;
  };

  const hideChecklist = async () => {
    if (await patchSetup({ checklistHidden: true }))
      gtag.event('checklist_hidden', {});
  };

  const markQrDone = () => {
    void patchSetup({ qrDone: true });
  };

  const goTo = (to: string) => {
    void router.push(to);
  };

  const viewAsDiner = () => {
    void storeLink.openAsDiner();
  };

  const shareStoreLink = async () => {
    if (await storeLink.copy())
      ui.showToast(
        AlertType.Success,
        String(proxy.$t('storeSetup.live.copied'))
      );
  };

  const loadHomeStats = async () => {
    try {
      const homeStats = await restaurantsService.getHomeStats(
        props.restaurant.id
      );
      itemCount.value = homeStats.itemCount;
      categoryCount.value = homeStats.categoryCount;
    } catch (error) {
      console.error('Error loading home stats:', error);
    }
  };

  /**
   * Single entry point for stats: resolves the branch business day ("Hoy" is
   * the BUSINESS day, cutoff-aware), shares it with the widgets through
   * `businessDay`, then loads the daily metric cards.
   */
  const loadStats = async () => {
    if (!hasStatsPermission.value) return;
    const restaurantId = props.restaurant?.id;
    const branchId = restaurantStore.selectedBranch?.id;
    if (restaurantId == null || branchId == null) {
      // Nothing to query (no accessible branch): drop any in-flight context
      // of the previous branch and never leave the cards spinning.
      businessDayGeneration++;
      Object.keys(dailyMetrics.value).forEach((key) => {
        dailyMetrics.value[key].loading = false;
      });
      return;
    }

    const generation = ++businessDayGeneration;
    let context: IStatsContext | null = null;
    try {
      context = await statsService.getContext(restaurantId, branchId);
    } catch {
      // The anchor falls back to the browser's local day.
    }
    // A newer branch switch already won.
    if (generation !== businessDayGeneration) return;

    const anchor = resolveBusinessDayAnchor(restaurantId, branchId, context);
    businessDay.value = anchor;
    await loadDailyMetrics(anchor);
  };

  const loadDailyMetrics = async (anchor: IBusinessDayAnchor) => {
    // Only load metrics if user has stats permission
    if (!hasStatsPermission.value) {
      return;
    }

    try {
      const today = anchor.today;

      // "Same time" comparisons measure hours elapsed since the business-day
      // start (cutoff), so post-midnight hours of a cutoff branch keep the
      // whole evening in scope. Cutoff 0 reduces to the plain hour compare.
      const cutoffHour = anchor.cutoffHour;
      const nowOffset = businessHourPosition(
        currentHourInTimeZone(anchor.timeZone),
        cutoffHour
      );
      const isUpToNow = (hour: number | undefined) =>
        businessHourPosition(Number(hour ?? 0), cutoffHour) <= nowOffset;

      // Determine comparison date based on store age
      const useWeekComparison = hasWeekOfData.value;
      const comparisonDate = new Date(today);

      if (useWeekComparison) {
        // Get same day last week
        comparisonDate.setDate(comparisonDate.getDate() - 7);
      } else {
        // Fall back to yesterday for new stores
        comparisonDate.setDate(comparisonDate.getDate() - 1);
      }

      // For orders, we still use full day ranges
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Load orders stats for today (with non-monetary breakdown to net out
      // invitaciones / consumo empleado)
      const todayOrdersPromise = statsService.getOrdersStats(
        anchor.restaurantId,
        anchor.branchId,
        today,
        undefined,
        undefined,
        undefined,
        undefined,
        true
      );

      // Load orders stats for comparison period
      const comparisonOrdersPromise = statsService.getOrdersStats(
        anchor.restaurantId,
        anchor.branchId,
        comparisonDate,
        undefined,
        undefined,
        undefined,
        undefined,
        true
      );

      // Load views stats for today (hourly, no "to" parameter)
      const todayViewsPromise = statsService.getViewsStats(
        anchor.restaurantId,
        anchor.branchId,
        today
      );

      // Load views stats for comparison period (hourly, no "to" parameter)
      const comparisonViewsPromise = statsService.getViewsStats(
        anchor.restaurantId,
        anchor.branchId,
        comparisonDate
      );

      // Load covers stats (closed table sessions) for today + comparison
      const todayCoversPromise = statsService.getCoversStats(
        anchor.restaurantId,
        anchor.branchId,
        today
      );

      const comparisonCoversPromise = statsService.getCoversStats(
        anchor.restaurantId,
        anchor.branchId,
        comparisonDate
      );

      // Execute all promises in parallel
      const [
        todayOrders,
        comparisonOrders,
        todayViews,
        comparisonViews,
        todayCovers,
        comparisonCovers,
      ] = await Promise.all([
        todayOrdersPromise.catch(() => null),
        comparisonOrdersPromise.catch(() => null),
        todayViewsPromise.catch(() => null),
        comparisonViewsPromise.catch(() => null),
        todayCoversPromise.catch(() => null),
        comparisonCoversPromise.catch(() => null),
      ]);

      // A newer branch switch re-anchored while these were in flight.
      if (businessDay.value !== anchor) return;

      // Process orders data - compare up to current hour. Revenue and average
      // ticket are NET of non-monetary payments (invitaciones / consumo
      // empleado) in BOTH periods; comped orders stay in the order count
      // (only their amounts are excluded).
      if (todayOrders && todayOrders.groups) {
        const sumGroupUpToNow = (
          stats: typeof todayOrders | null,
          groupId: string
        ) =>
          (stats?.groups.find((g) => g.id === groupId)?.stats || [])
            .filter((s) => isUpToNow(s.hour))
            .reduce((sum, s) => sum + s.value, 0);

        const todayOrderCount = sumGroupUpToNow(todayOrders, 'count');
        const todayGrossRevenue = sumGroupUpToNow(todayOrders, 'total');
        const todayNonMonetary = sumGroupUpToNow(
          todayOrders,
          'nonMonetaryTotal'
        );
        const todayRevenue = Math.max(0, todayGrossRevenue - todayNonMonetary);

        const todayAverage =
          todayOrderCount > 0 ? todayRevenue / todayOrderCount : 0;

        const comparisonOrderCount = sumGroupUpToNow(comparisonOrders, 'count');
        const comparisonGrossRevenue = sumGroupUpToNow(
          comparisonOrders,
          'total'
        );
        const comparisonNonMonetary = sumGroupUpToNow(
          comparisonOrders,
          'nonMonetaryTotal'
        );
        const comparisonRevenue = Math.max(
          0,
          comparisonGrossRevenue - comparisonNonMonetary
        );

        const comparisonAverage =
          comparisonOrderCount > 0
            ? comparisonRevenue / comparisonOrderCount
            : 0;

        dailyMetrics.value.orders = {
          count: todayOrderCount,
          changeFromLastWeek: todayOrderCount - comparisonOrderCount,
          loading: false,
          comparisonType: useWeekComparison ? 'week' : 'day',
        };

        dailyMetrics.value.revenue = {
          amount: todayRevenue,
          changeFromLastWeek: todayRevenue - comparisonRevenue,
          loading: false,
          comparisonType: useWeekComparison ? 'week' : 'day',
          nonMonetary: todayNonMonetary,
          gross: todayGrossRevenue,
        };

        dailyMetrics.value.averageTicket = {
          amount: todayAverage,
          changeFromLastWeek: todayAverage - comparisonAverage,
          loading: false,
          comparisonType: useWeekComparison ? 'week' : 'day',
        };
      } else {
        // Set to zero if no data
        dailyMetrics.value.orders.loading = false;
        dailyMetrics.value.revenue.loading = false;
        dailyMetrics.value.averageTicket.loading = false;
      }

      // Process views data - compare up to current hour
      if (todayViews && todayViews.groups) {
        // Sum all groups (links, tables, otherQr, etc.) for today
        let todayViewCount = 0;
        todayViews.groups.forEach((group) => {
          if (group.stats) {
            const groupSum = group.stats
              .filter((s) => isUpToNow(s.hour))
              .reduce((sum, s) => sum + s.value, 0);
            todayViewCount += groupSum;
          }
        });

        // Sum all groups for comparison period
        let comparisonViewCount = 0;
        if (comparisonViews && comparisonViews.groups) {
          comparisonViews.groups.forEach((group) => {
            if (group.stats) {
              const groupSum = group.stats
                .filter((s) => isUpToNow(s.hour))
                .reduce((sum, s) => sum + s.value, 0);
              comparisonViewCount += groupSum;
            }
          });
        }

        dailyMetrics.value.menuViews = {
          count: todayViewCount,
          changeFromLastWeek: todayViewCount - comparisonViewCount,
          loading: false,
          comparisonType: useWeekComparison ? 'week' : 'day',
        };
      } else {
        dailyMetrics.value.menuViews.loading = false;
      }

      // Process covers data - compare up to current hour. spendPerCover is a
      // per-bucket average (revenue/covers), so the day-level value is the
      // covers-weighted average, which reconstructs total revenue / covers.
      const sumCoversMetrics = (stats: typeof todayCovers) => {
        const groupSumUpToNow = (id: string) =>
          (stats?.groups.find((g) => g.id === id)?.stats || [])
            .filter((s) => isUpToNow(s.hour))
            .reduce((sum, s) => sum + s.value, 0);

        const coversStats =
          stats?.groups.find((g) => g.id === 'covers')?.stats || [];
        const spendStats =
          stats?.groups.find((g) => g.id === 'spendPerCover')?.stats || [];
        const spendByHour = new Map(spendStats.map((s) => [s.hour, s.value]));

        let covers = 0;
        let revenue = 0;
        for (const s of coversStats) {
          if (!isUpToNow(s.hour)) continue;
          covers += s.value;
          revenue += s.value * (Number(spendByHour.get(s.hour)) || 0);
        }
        return {
          covers,
          spendPerCover: covers > 0 ? revenue / covers : 0,
          sittings: groupSumUpToNow('sittings'),
          sittingsWithCovers: groupSumUpToNow('sittingsWithCovers'),
          nonMonetary: groupSumUpToNow('nonMonetaryWithCovers'),
        };
      };

      if (todayCovers && todayCovers.groups) {
        const todayMetrics = sumCoversMetrics(todayCovers);
        const comparisonMetrics = sumCoversMetrics(comparisonCovers);

        dailyMetrics.value.covers = {
          count: todayMetrics.covers,
          changeFromLastWeek: todayMetrics.covers - comparisonMetrics.covers,
          loading: false,
          comparisonType: useWeekComparison ? 'week' : 'day',
          incomplete: todayMetrics.sittingsWithCovers < todayMetrics.sittings,
        };

        dailyMetrics.value.spendPerCover = {
          amount: todayMetrics.spendPerCover,
          changeFromLastWeek:
            todayMetrics.spendPerCover - comparisonMetrics.spendPerCover,
          loading: false,
          comparisonType: useWeekComparison ? 'week' : 'day',
          nonMonetary: todayMetrics.nonMonetary,
        };

        showCoversCards.value =
          todayMetrics.covers > 0 || comparisonMetrics.covers > 0;
      } else {
        dailyMetrics.value.covers.loading = false;
        dailyMetrics.value.spendPerCover.loading = false;
        showCoversCards.value = false;
      }
    } catch (error) {
      if (businessDay.value !== anchor) return;
      console.error('Error loading daily metrics:', error);
      // Set all to not loading on error
      Object.keys(dailyMetrics.value).forEach((key) => {
        dailyMetrics.value[key].loading = false;
      });
    }
  };

  // Watch for branch changes
  watch(
    () => restaurantStore.selectedBranch,
    async (newBranch, oldBranch) => {
      if (
        newBranch &&
        newBranch.id !== oldBranch?.id &&
        hasStatsPermission.value
      ) {
        await loadStats();
      }
    }
  );

  // Another store in the same page: its own setup state.
  watch(
    () => props.restaurant?.id ?? null,
    (restaurantId, previousId) => {
      if (restaurantId !== null && restaurantId !== previousId)
        void loadSetupStatus();
    }
  );

  // Lifecycle
  onBeforeUnmount(() => {
    alive = false;
  });

  onMounted(async () => {
    void loadSetupStatus();
    loading.value = true;
    const promises = [loadHomeStats()];

    // Only load daily metrics if user has stats permission
    if (hasStatsPermission.value) {
      promises.push(loadStats());
    }

    await Promise.all(promises);
    loading.value = false;
  });
</script>

<template>
  <div ref="root" class="v2-dashboard" :class="layoutClasses">
    <!-- Header Section -->
    <div class="dashboard-header">
      <V2RestaurantDashboardHeader
        :item-count="itemCount"
        :category-count="categoryCount"
      />
    </div>

    <!-- Branch Selector (if multiple branches) -->
    <div
      v-if="restaurantStore.visibleBranches?.length > 1"
      class="branch-selector-wrapper"
    >
      <V2RestaurantDashboardBranchSelector />
    </div>

    <!-- Main Dashboard Content -->
    <div class="dashboard-content">
      <!-- Welcome Title - Always on top -->
      <div class="welcome-section">
        <h2 class="section-title">
          <v-icon>mdi-handshake</v-icon>
          {{ $t('dashboard.welcomeBack') }}
        </h2>
        <V2StatsBusinessDayNote
          v-if="businessDayNote"
          :context="businessDayNote.context"
          :branch-id="businessDayNote.branchId"
        />
      </div>

      <!-- Reorderable Content Wrapper -->
      <div class="reorderable-content">
        <!-- Today's Metrics - Only show if user has stats permission -->
        <div v-if="hasStatsPermission" class="metrics-section">
          <div class="metrics-grid">
            <V2RestaurantDashboardMetricCard
              :title="String($t('dashboard.ordersToday'))"
              :value="dailyMetrics.orders.count"
              :change="dailyMetrics.orders.changeFromLastWeek"
              :loading="dailyMetrics.orders.loading"
              icon="mdi-receipt"
              :to="`/${restaurant.stringId}/stats/orders`"
              :change-label="
                getComparisonLabel(dailyMetrics.orders.comparisonType)
              "
              :empty-text="emptyText(emptyMetrics.orders)"
            />
            <V2RestaurantDashboardMetricCard
              :title="String($t('dashboard.revenueToday'))"
              :value="`${
                restaurantStore.currency?.symbol || '$'
              }${dailyMetrics.revenue.amount.toFixed(2)}`"
              :change="dailyMetrics.revenue.changeFromLastWeek"
              :loading="dailyMetrics.revenue.loading"
              icon="mdi-cash-multiple"
              :to="`/${restaurant.stringId}/stats/paymentMethods`"
              is-currency
              :change-label="
                getComparisonLabel(dailyMetrics.revenue.comparisonType)
              "
              :value-info="revenueInfoText"
              value-info-color="warning"
              :empty-text="emptyText(emptyMetrics.revenue)"
            />
            <V2RestaurantDashboardMetricCard
              :title="String($t('dashboard.menuViewsToday'))"
              :value="dailyMetrics.menuViews.count"
              :change="dailyMetrics.menuViews.changeFromLastWeek"
              :loading="dailyMetrics.menuViews.loading"
              icon="mdi-eye"
              :to="`/${restaurant.stringId}/stats/views`"
              :change-label="
                getComparisonLabel(dailyMetrics.menuViews.comparisonType)
              "
              :empty-text="emptyText(emptyMetrics.views)"
              :empty-action-label="
                emptyMetrics.views
                  ? String($t('storeSetup.dashboard.share'))
                  : undefined
              "
              @empty-action="shareStoreLink"
            />
            <V2RestaurantDashboardMetricCard
              :title="String($t('dashboard.averageTicket'))"
              :value="`${
                restaurantStore.currency?.symbol || '$'
              }${dailyMetrics.averageTicket.amount.toFixed(2)}`"
              :change="dailyMetrics.averageTicket.changeFromLastWeek"
              :loading="dailyMetrics.averageTicket.loading"
              icon="mdi-calculator"
              :to="`/${restaurant.stringId}/stats/orders`"
              is-currency
              :change-label="
                getComparisonLabel(dailyMetrics.averageTicket.comparisonType)
              "
              :value-info="averageTicketInfoText"
              value-info-color="warning"
            />
            <V2RestaurantDashboardMetricCard
              v-if="showCoversCards"
              :title="String($t('dashboard.coversToday'))"
              :value="coversDisplayValue"
              :change="dailyMetrics.covers.changeFromLastWeek"
              :loading="dailyMetrics.covers.loading"
              icon="mdi-account-group"
              :change-label="
                getComparisonLabel(dailyMetrics.covers.comparisonType)
              "
              :value-info="
                dailyMetrics.covers.incomplete
                  ? String($t('dashboard.coversIncomplete'))
                  : undefined
              "
            />
            <V2RestaurantDashboardMetricCard
              v-if="showCoversCards"
              :title="String($t('dashboard.spendPerCover'))"
              :value="`${
                restaurantStore.currency?.symbol || '$'
              }${dailyMetrics.spendPerCover.amount.toFixed(2)}`"
              :change="dailyMetrics.spendPerCover.changeFromLastWeek"
              :loading="dailyMetrics.spendPerCover.loading"
              icon="mdi-account-cash"
              is-currency
              :change-label="
                getComparisonLabel(dailyMetrics.spendPerCover.comparisonType)
              "
              :value-info="spendPerCoverInfoText"
              value-info-color="warning"
            />
          </div>
        </div>

        <!-- Messages Section (System & Onboarding) - Reordered on mobile -->
        <div class="messages-wrapper">
          <!-- System Messages -->
          <V2RestaurantDashboardSystemMessages />

          <!-- GPTHelp Promo -->
          <V2RestaurantDashboardGpthelpPromo />

          <!-- Setup checklist (flow stores) or Onboarding & Messages -->
          <V2StoreSetupChecklist
            v-if="showChecklist"
            :rows="checklistRows"
            :busy="setupBusy"
            @hide="hideChecklist"
            @qr-done="markQrDone"
            @go="goTo"
          />
          <V2RestaurantDashboardOnboardingMessages
            v-else-if="showOnboarding"
            :is-new-store="isNewStore"
            :item-count="itemCount"
            :category-count="categoryCount"
          />
        </div>

        <!-- Main Grid Layout -->
        <div class="main-grid">
          <!-- Left Column -->
          <div class="left-column">
            <!-- «Su menú» -->
            <V2StoreSetupMenuCard
              v-if="showMenuCard && setupState"
              :status="setupState"
              :restaurant="restaurant"
              :multiple-menus="multipleMenus"
              @go="goTo"
              @view-as-diner="viewAsDiner"
              @undo-update="undoMenuUpdate"
            />

            <!-- Operations & Quick Actions Row -->
            <div class="operations-row">
              <V2RestaurantDashboardOrderOpsStatus />
              <V2RestaurantDashboardQuickActions />
            </div>

            <!-- Performance Chart - Only show if user has stats permission -->
            <V2RestaurantDashboardPerformanceChart
              v-if="hasStatsPermission"
              :business-day="businessDay"
            />

            <!-- Top & Bottom Items - Only show if user has stats permission -->
            <V2RestaurantDashboardTopBottomItems
              v-if="hasStatsPermission"
              :business-day="businessDay"
            />

            <!-- Heatmap - Only show if user has stats permission -->
            <V2RestaurantDashboardHeatmap
              v-if="hasStatsPermission"
              :business-day="businessDay"
            />
          </div>

          <!-- Right Column - Only visible on larger screens -->
          <div class="right-column">
            <!-- System Messages -->
            <V2RestaurantDashboardSystemMessages />

            <!-- GPTHelp Promo -->
            <V2RestaurantDashboardGpthelpPromo />

            <!-- Setup checklist (flow stores) or Onboarding & Messages -->
            <V2StoreSetupChecklist
              v-if="showChecklist"
              :rows="checklistRows"
              :busy="setupBusy"
              @hide="hideChecklist"
              @qr-done="markQrDone"
              @go="goTo"
            />
            <V2RestaurantDashboardOnboardingMessages
              v-else-if="showOnboarding"
              :is-new-store="isNewStore"
              :item-count="itemCount"
              :category-count="categoryCount"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // The is-sm, is-md, is-lg and is-xl steps come from the page's own width
  // (dashboard-layout.ts) and work like min-width queries.
  .v2-dashboard {
    @apply tw-min-h-screen tw-p-2;
    @apply tw-overflow-x-hidden;

    &.is-sm {
      @apply tw-p-4;
    }

    &.is-xl {
      @apply tw-p-6;
    }

    .dashboard-header {
      @apply tw-mb-4;
    }

    .branch-selector-wrapper {
      @apply tw-mb-4;
    }

    .dashboard-content {
      @apply tw-space-y-4;
      @apply tw-w-full tw-overflow-x-hidden;
    }

    .section-title {
      @apply tw-text-2xl tw-font-semibold tw-text-gray-800 tw-mb-3;
    }

    // Title left, business-day note pushed right on the same row; on narrow
    // screens the note wraps below the title, left-aligned (space-between
    // starts a lone wrapped item at the left).
    .welcome-section {
      @apply tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between;

      .section-title {
        @apply tw-mb-0 tw-mr-3;
      }
    }

    .reorderable-content {
      @apply tw-flex tw-flex-col tw-gap-4;
    }

    .metrics-section {
      @apply tw-w-full;

      .metrics-grid {
        @apply tw-grid tw-grid-cols-1 tw-gap-3;
      }
    }

    .messages-wrapper {
      @apply tw-w-full tw-space-y-4;
    }

    .main-grid {
      @apply tw-grid tw-grid-cols-1 tw-gap-4;

      .left-column {
        @apply tw-space-y-4 tw-min-w-0;

        .operations-row {
          @apply tw-grid tw-grid-cols-1 tw-gap-4 tw-min-w-0;
        }
      }

      .right-column {
        @apply tw-hidden tw-space-y-4 tw-min-w-0;
      }
    }

    // Phones: every widget stays inside the page.
    &:not(.is-sm) {
      .dashboard-content > * {
        @apply tw-max-w-full;
      }

      .main-grid {
        .left-column,
        .right-column {
          @apply tw-w-full;

          > * {
            @apply tw-max-w-full;
          }
        }
      }
    }

    &.is-sm .metrics-section .metrics-grid {
      @apply tw-grid-cols-2;
    }

    &.is-md .main-grid .left-column .operations-row {
      @apply tw-grid-cols-2;
    }

    // The messages move from above the grid to its right column.
    &.is-lg {
      .metrics-section .metrics-grid {
        /* 6 cards: 3x2 keeps the rows balanced (was 4 cards in one row). */
        @apply tw-grid-cols-3;
      }

      .messages-wrapper {
        @apply tw-hidden;
      }

      .main-grid {
        @apply tw-grid-cols-3;

        .left-column {
          @apply tw-col-span-2;
        }

        .right-column {
          @apply tw-block tw-col-span-1;
        }
      }
    }

    &.is-xl .main-grid {
      @apply tw-grid-cols-4;

      .left-column {
        @apply tw-col-span-3;
      }
    }
  }

  // Ultra-wide screens
  @media (min-width: 1536px) {
    .v2-dashboard {
      @apply tw-max-w-screen-2xl tw-mx-auto;
    }
  }
</style>
